// Hybrid AI Beta Read-Only (Owner). Hanya info/peringatan; tidak mengubah data produksi.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, RefreshCw, ShieldCheck, AlertTriangle } from "lucide-react";
import { AiFailureAudit } from "../ai_read_audit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ai-beta`;
const ax = (cfg) => axios({ withCredentials: true, ...cfg });
const errMsg = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : (e?.message || "Gagal"); };
const rp = (n) => `Rp${Number(n || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 })}`;
const STATUS = { queued: "Antre", processing: "Diproses", done: "Selesai", skipped: "Dilewati", failed: "Gagal" };
const SUMMARY = { peringatan: ["Peringatan", "#b45309"], info: ["Info", "#475569"], sesuai: ["Sesuai", "#15803d"] };

function ToggleCard({ st, onSave, busy }) {
  const s = st.settings;
  const [daily, setDaily] = useState(s.daily_budget_idr);
  const [monthly, setMonthly] = useState(s.monthly_budget_idr);
  useEffect(() => { setDaily(s.daily_budget_idr); setMonthly(s.monthly_budget_idr); }, [s.daily_budget_idr, s.monthly_budget_idr]);
  const save = (enabled) => onSave({ enabled, daily_budget_idr: Number(daily), monthly_budget_idr: Number(monthly) });
  return <div className="card" style={{ padding: 12, display: "grid", gap: 8 }} data-testid="ai-beta-toggle-card">
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <b>Status AI:</b>
      <span data-testid="ai-beta-enabled-state" style={{ fontWeight: 700, color: s.enabled ? "#15803d" : "#b91c1c" }}>{s.enabled ? "ON" : "OFF"}</span>
      <button className={s.enabled ? "soft-btn small-btn" : "primary small-btn"} disabled={busy} onClick={() => save(!s.enabled)}
        data-testid="ai-beta-toggle-btn">{s.enabled ? "Matikan AI" : "Aktifkan AI"}</button>
      {s.updated_by && <small style={{ color: "#64748b" }}>Terakhir diubah {s.updated_by}</small>}
    </div>
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end" }}>
      <label style={{ fontSize: 13 }}>Batas harian (maks {rp(st.caps.daily)})<br/>
        <input type="number" min={0} max={st.caps.daily} value={daily} onChange={e => setDaily(e.target.value)} data-testid="ai-beta-daily-input"/></label>
      <label style={{ fontSize: 13 }}>Batas bulanan (maks {rp(st.caps.monthly)})<br/>
        <input type="number" min={0} max={st.caps.monthly} value={monthly} onChange={e => setMonthly(e.target.value)} data-testid="ai-beta-monthly-input"/></label>
      <button className="soft-btn small-btn" disabled={busy} onClick={() => save(s.enabled)} data-testid="ai-beta-save-budget-btn">Simpan batas</button>
    </div>
  </div>;
}

function StatsRow({ st }) {
  const u = st.usage, r = st.readiness;
  const item = (label, val, id) => <div className="card" style={{ padding: 10, minWidth: 150 }}><small>{label}</small><div style={{ fontWeight: 700 }} data-testid={id}>{val}</div></div>;
  return <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
    {item("Biaya hari ini", `${rp(u.today_idr)} / ${rp(st.settings.daily_budget_idr)}`, "ai-beta-usage-today")}
    {item("Biaya bulan ini", `${rp(u.month_idr)} / ${rp(st.settings.monthly_budget_idr)}`, "ai-beta-usage-month")}
    {item("Peringatan", st.warnings, "ai-beta-warning-count")}
    {item("Kesiapan privasi", r.ready === null ? "Dicek saat AI ON" : r.ready ? "Siap (OCR + barcode)" : "Tidak siap — gambar tidak dikirim", "ai-beta-readiness")}
  </div>;
}

function ResultRow({ r }) {
  const [lab, color] = SUMMARY[r.summary] || [STATUS[r.status] || r.status, "#64748b"];
  const target = r.source === "offline" ? `${r.order_number}` : `${r.order_number} · v${r.version}`;
  const notes = (r.findings || []).filter(f => f.level === "peringatan").concat((r.findings || []).filter(f => f.level === "info"));
  return <tr data-testid={`ai-beta-row-${r.id}`}>
    <td style={{ whiteSpace: "nowrap" }}>{String(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
    <td>{r.source === "offline" ? "Offline" : "Marketplace"}<br/><small>{target}</small><br/><small style={{ color: "#94a3b8" }}>{r.filename}</small></td>
    <td><span style={{ color, fontWeight: 700 }} data-testid={`ai-beta-row-status-${r.id}`}>{lab}</span>{r.status !== "done" && r.reason ? <><br/><small>{r.reason}</small></> : null}</td>
    <td style={{ fontSize: 12 }}>{notes.length ? notes.slice(0, 4).map((f, i) => <div key={i} style={{ color: f.level === "peringatan" ? "#b45309" : "#475569" }}>{f.level === "peringatan" ? <AlertTriangle size={11}/> : "• "} {f.message}</div>) : (r.status === "done" ? "Lengan, warna & jenis sesuai teks pesanan" : "—")}</td>
  </tr>;
}

function MockupReadToggle() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/ai-mockup-read/settings`;
  const load = useCallback(() => ax({ url }).then(r => setD(r.data)).catch(e => setErr(errMsg(e))), [url]);
  useEffect(() => { load(); }, [load]);
  const flip = async () => {
    if (!d.enabled && !window.confirm("Aktifkan Baca Mockup untuk Gudang? Memakai anggaran AI Beta yang sama.")) return;
    setBusy(true);
    try { await ax({ method: "put", url, data: { enabled: !d.enabled } }); await load(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  if (!d) return err ? <div className="card" style={{ padding: 10 }} data-testid="mockup-read-error">{err}</div> : null;
  return <div className="card" style={{ padding: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }} data-testid="mockup-read-toggle-card">
    <b>Baca Mockup untuk Gudang</b>
    <span data-testid="mockup-read-state" style={{ fontWeight: 700, color: d.enabled ? "#15803d" : "#b91c1c" }}>{d.enabled ? "ON" : "OFF"}</span>
    <button className={d.enabled ? "soft-btn small-btn" : "primary small-btn"} disabled={busy} onClick={flip} data-testid="mockup-read-toggle-btn">{d.enabled ? "Matikan" : "Aktifkan"}</button>
    <span className="muted small">Bantuan rangkuman barang untuk Rido · anggaran bersama AI Beta (hari ini Rp{d.usage_shared_with_ai_beta?.today_idr ?? 0}) · stok tidak dipotong</span>
    {d.monitor && <span className="small" data-testid="mockup-read-monitor">Diperiksa <b>{d.monitor.checked}</b> · Gagal <b>{d.monitor.failed}</b> · Laporan Hasil AI Salah <b>{d.monitor.wrong_reports}</b> · Biaya <b>Rp{d.monitor.cost_idr}</b></span>}
    {err && <span className="small" style={{ color: "#b91c1c" }}>{err}</span>}
    <div style={{ flexBasis: "100%" }}><AiFailureAudit/></div>
  </div>;
}

const CUT_LABELS = { cut: "Dipotong", manual_done: "Sudah manual", unverified: "AI ragu", perlu_manual: "Sebagian manual", failed: "Gagal/stok kurang" };

function AccStockCutToggle() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/acc-stock-cut/settings`;
  const load = useCallback(() => ax({ url }).then(r => setD(r.data)).catch(e => setErr(errMsg(e))), [url]);
  useEffect(() => { load(); }, [load]);
  const flip = async () => {
    if (!d.enabled && !window.confirm("Aktifkan potong stok otomatis saat ACC Adit? Stok hanya dipotong bila hasil AI mockup final pasti (semua OK).")) return;
    setBusy(true);
    try { await ax({ method: "put", url, data: { enabled: !d.enabled } }); await load(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  if (!d) return err ? <div className="card" style={{ padding: 10 }} data-testid="acc-stock-cut-error">{err}</div> : null;
  const counts = Object.entries(d.counts || {});
  return <div className="card" style={{ padding: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }} data-testid="acc-stock-cut-toggle-card">
    <b>Potong stok otomatis saat ACC Adit</b>
    <span data-testid="acc-stock-cut-state" style={{ fontWeight: 700, color: d.enabled ? "#15803d" : "#b91c1c" }}>{d.enabled ? "ON" : "OFF"}</span>
    <button className={d.enabled ? "soft-btn small-btn" : "primary small-btn"} disabled={busy} onClick={flip} data-testid="acc-stock-cut-toggle-btn">{d.enabled ? "Matikan" : "Aktifkan"}</button>
    <span className="muted small">Memakai Barang Keluar · hanya bila AI pasti · AI ragu / stok kurang → "Perlu Potong Manual"</span>
    {counts.length > 0 && <span className="small" data-testid="acc-stock-cut-counts">{counts.map(([k, n]) => `${CUT_LABELS[k] || k} ${n}`).join(" · ")}</span>}
    {err && <span className="small" style={{ color: "#b91c1c" }}>{err}</span>}
  </div>;
}

// Tanggal Batas Kirim Otomatis di header cetak mockup — hanya mockup yang diunggah SETELAH diaktifkan.
function ShipDateHeaderToggle() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/ship-date-header/settings`;
  const load = useCallback(() => ax({ url }).then(r => setD(r.data)).catch(e => setErr(errMsg(e))), [url]);
  useEffect(() => { load(); }, [load]);
  const flip = async () => {
    if (!d.enabled && !window.confirm("Aktifkan Tanggal Batas Kirim Otomatis? Hanya mockup yang diunggah SETELAH ini yang mendapat tanggal merah di atas; mockup lama tetap seperti sebelumnya. Operator tidak perlu lagi menulis tanggal di CorelDRAW.")) return;
    setBusy(true);
    try { await ax({ method: "put", url, data: { enabled: !d.enabled } }); await load(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  if (!d) return err ? <div className="card" style={{ padding: 10 }} data-testid="ship-date-header-error">{err}</div> : null;
  return <div className="card" style={{ padding: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }} data-testid="ship-date-header-card">
    <b>Tanggal Batas Kirim Otomatis</b>
    <span data-testid="ship-date-header-state" style={{ fontWeight: 700, color: d.enabled ? "#15803d" : "#b91c1c" }}>{d.enabled ? "ON" : "OFF"}</span>
    <button className={d.enabled ? "soft-btn small-btn" : "primary small-btn"} disabled={busy} onClick={flip} data-testid="ship-date-header-toggle-btn">{d.enabled ? "Matikan" : "Aktifkan"}</button>
    <span className="muted small">Tanggal merah di atas cetakan mockup · Shopee/Tokopedia: batas kirim · Offline: deadline nota · tanpa data → "BELUM ADA BATAS KIRIM"
      {d.enabled && d.since ? ` · berlaku untuk mockup yang diunggah sejak ${new Date(d.since).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })} WIB` : ""}</span>
    {err && <span className="small" style={{ color: "#b91c1c" }}>{err}</span>}
  </div>;
}

export default function AiBetaPanel() {
  const [st, setSt] = useState(null);
  const [rows, setRows] = useState({ total: 0, rows: [] });
  const [onlyWarn, setOnlyWarn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([ax({ url: `${API}/status` }), ax({ url: `${API}/results`, params: { limit: 50, only_warnings: onlyWarn ? 1 : 0 } })]);
      setSt(a.data); setRows(b.data); setErr("");
    } catch (e) { setErr(errMsg(e)); }
  }, [onlyWarn]);
  useEffect(() => { load(); }, [load]);
  const save = async (body) => {
    setBusy(true);
    try { await ax({ method: "put", url: `${API}/settings`, data: body }); await load(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  return <div style={{ display: "grid", gap: 12 }} data-testid="ai-beta-panel">
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Sparkles size={20}/><h2 style={{ margin: 0 }}>AI Beta — Cek Mockup (Read-Only)</h2>
      <button className="soft-btn small-btn" onClick={load} data-testid="ai-beta-refresh-btn"><RefreshCw size={13}/> Muat ulang</button>
    </div>
    <div className="card" style={{ padding: 10, fontSize: 13, background: "#f8fafc" }} data-testid="ai-beta-disclaimer">
      <ShieldCheck size={14}/> AI hanya membaca salinan mockup yang sudah disamarkan dan memberi info/peringatan. AI tidak mengubah pesanan, mockup, stok, status, ACC/FIX, penggajian, atau riwayat produksi. Hasil hanya untuk evaluasi.
    </div>
    {err && <div className="card" style={{ padding: 10, color: "#b91c1c" }} data-testid="ai-beta-error"><AlertTriangle size={14}/> {err}</div>}
    {st && <><ToggleCard st={st} onSave={save} busy={busy}/><StatsRow st={st}/></>}
    <MockupReadToggle />
    <AccStockCutToggle />
    <ShipDateHeaderToggle />
    <label style={{ fontSize: 13 }}><input type="checkbox" checked={onlyWarn} onChange={e => setOnlyWarn(e.target.checked)} data-testid="ai-beta-only-warnings"/> Hanya peringatan</label>
    <div className="card" style={{ padding: 0, overflowX: "auto" }}>
      <table className="table" style={{ width: "100%" }} data-testid="ai-beta-results-table">
        <thead><tr><th>Waktu</th><th>File</th><th>Hasil</th><th>Catatan AI</th></tr></thead>
        <tbody>{rows.rows.length ? rows.rows.map(r => <ResultRow key={r.id} r={r}/>) : <tr><td colSpan={4} data-testid="ai-beta-empty">Belum ada hasil.</td></tr>}</tbody>
      </table>
    </div>
    <small style={{ color: "#64748b" }} data-testid="ai-beta-total">Total {rows.total} hasil</small>
  </div>;
}
